import ApiService from './api.service';
import { API_PREFIX } from '../config/appConfig';
import { UserRegistrationData } from '../types/models/User';
import { AuthResponse } from '../types/models/AuthResponse';

const AuthService = {
  // Funzioni centralizzate per gestire localStorage
  saveAuthData: (response) => {
    localStorage.setItem('auth_token', response.token);
    localStorage.setItem('user_role', response.user.role);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem('has_survey', response.has_survey?.toString() || 'false');
  },

  clearAuthData: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user');
    localStorage.removeItem('has_survey');
  },

  sendRegistrationOtp: async (phone) => {
    const response = await fetch(`${API_PREFIX}/send-registration-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ phone })
    });
    return response.json();
  },

  completeRegistration: async (userData) => {
    const mappedData = new UserRegistrationData({
      first_name: userData.nome,
      last_name: userData.cognome,
      email: userData.email,
      password: userData.password,
      phone: userData.phone || '',
      gender: userData.gender || 'OTHER',
      tag_id: userData.tag_id || null,
      otp: userData.otp,
      city: userData.city || ''
    });

    console.log('AuthService - completeRegistration - dati mappati:', mappedData);

    const response = await fetch(`${API_PREFIX}/complete-registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(mappedData)
    });

    return response.json();
  },

  checkDuplicates: async (email, phone) => {
    const response = await fetch(`${API_PREFIX}/check-duplicates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, phone })
    });
    return await response.json();
  },

  register: async (userData) => {
    console.log('%c [DEBUG] AuthService - register ENTER', 'background: #222; color: #bada55', {
      userData
    });

    try {
      const mappedData = new UserRegistrationData({
        first_name: userData.nome,
        last_name: userData.cognome,
        email: userData.email,
        password: userData.password,
        phone: userData.phone || '',
        gender: userData.gender || 'OTHER',
        tag_id: userData.tag_id || null
      });

      const response = await fetch(`${API_PREFIX}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mappedData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || Object.values(data.errors)[0][0]);
      }

      console.log('%c [DEBUG] AuthService - register SUCCESS', 'background: #222; color: #01FF70', {
        response: data
      });

      // Usa la funzione centralizzata
      if (data.token && !data.requires_verification) {
        AuthService.saveAuthData(data);
      }

      return data;
    } catch (error) {
      console.error('%c [DEBUG] AuthService - register ERROR', 'background: #222; color: #FF4136', {
        error: error.message
      });
      throw error;
    }
  },

  verifyOtp: async (phone, otp) => {
    console.log('%c [DEBUG] AuthService - verifyOtp ENTER', 'background: #222; color: #bada55', {
      phone,
      otp
    });

    try {
      const response = await fetch(`${API_PREFIX}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ phone, otp })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Errore nella verifica OTP');
      }

      console.log('%c [DEBUG] AuthService - verifyOtp SUCCESS', 'background: #222; color: #01FF70', {
        response: data
      });

      // Usa la funzione centralizzata
      if (data.token) {
        AuthService.saveAuthData(data);
      }

      return data;
    } catch (error) {
      console.error('%c [DEBUG] AuthService - verifyOtp ERROR', 'background: #222; color: #FF4136', {
        error: error.message
      });
      throw error;
    }
  },

  // Login standard (pazienti e dottori)
  login: async (credentials) => {
    try {
      const response = await fetch(`${API_PREFIX}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Errore durante il login');
      }

      const authResponse = new AuthResponse(data);

      // Usa la funzione centralizzata
      AuthService.saveAuthData(authResponse);

      console.log('%c [DEBUG] AuthService - login SUCCESS', 'background: #222; color: #01FF70', {
        response: authResponse
      });

      return authResponse;
    } catch (error) {
      console.error('%c [DEBUG] AuthService - login ERROR', 'background: #222; color: #FF4136', {
        error: error.message
      });
      throw error;
    }
  },

  // Login admin (rotta separata)
  adminLogin: async (credentials) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Errore durante il login');
      }

      // Usa la funzione centralizzata
      AuthService.saveAuthData(data);

      console.log('%c [DEBUG] AuthService - adminLogin SUCCESS', 'background: #222; color: #01FF70', {
        response: data
      });

      return data;
    } catch (error) {
      console.error('%c [DEBUG] AuthService - adminLogin ERROR', 'background: #222; color: #FF4136', {
        error: error.message
      });
      throw error;
    }
  },

  // Logout con chiamata API + pulizia localStorage
  logout: async () => {
    console.log('[DEBUG] AuthService - logout ENTER');
    try {
      const token = AuthService.getToken();
      if (token) {
        await ApiService.fetch('logout', {
          method: 'POST'
        });
      }
    } catch (error) {
      console.error('[DEBUG] AuthService - logout API error:', error);
      // Continuiamo comunque a pulire il localStorage anche se l'API fallisce
    } finally {
      // Usa la funzione centralizzata
      AuthService.clearAuthData();
    }
  },

  // Logout admin con rotta specifica
  adminLogout: async () => {
    console.log('[DEBUG] AuthService - adminLogout ENTER');
    try {
      const token = AuthService.getToken();
      if (token) {
        await fetch(`${API_PREFIX}/admin/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('[DEBUG] AuthService - adminLogout API error:', error);
    } finally {
      AuthService.clearAuthData();
    }
  },

  // Verifica autenticazione
  isAuthenticated: () => {
    const hasToken = !!localStorage.getItem('auth_token');
    console.log('[DEBUG] AuthService - isAuthenticated:', hasToken);
    return hasToken;
  },

  // Recupero token
  getToken: () => {
    const token = localStorage.getItem('auth_token');
    console.log('[DEBUG] AuthService - getToken:', token ? 'Token exists' : 'No token');
    return token;
  },

  // Recupero ruolo utente
  getUserRole: () => {
    return localStorage.getItem('user_role');
  },

  // Recupero ID utente corrente
  getCurrentUserId: () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id;
  },

  // Recupero utente dal localStorage
  getUserFromStorage: () => {
    try {
      const userString = localStorage.getItem('user');
      if (!userString) {
        return null;
      }
      return JSON.parse(userString);
    } catch (error) {
      console.error('[DEBUG] AuthService - getUserFromStorage ERROR:', error);
      return null;
    }
  },

  // Recupero commission_rate del therapist
  getCommissionRate: () => {
    try {
      const user = AuthService.getUserFromStorage();
      return user?.therapist?.commission_rate || 75; // Default 75%
    } catch (error) {
      console.error('[DEBUG] AuthService - getCommissionRate ERROR:', error);
      return 75;
    }
  },
};

export default AuthService;